import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Select, 
  MenuItem, 
  FormControl,
  Typography,
  Button,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import TranslateIcon from '@mui/icons-material/Translate';

const useStyles = makeStyles((theme) => ({
  languageSwitcher: {
    minWidth: 120,
    marginRight: theme.spacing(2),
  },
  select: {
    '& .MuiSelect-select': {
      paddingTop: 5,
      paddingBottom: 5,
    },
  },
  loginSelect: {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.primary.main,
      '& fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  languageButton: {
    textTransform: 'none',
    borderRadius: '20px',
    padding: '6px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      background: theme.palette.primary.light,
    },
  },
  loginLanguageButton: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.04)',
      borderColor: theme.palette.primary.dark,
    },
  },
  menuItem: {
    padding: '10px 16px',
    minWidth: '150px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  menuPaper: {
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    marginTop: '8px',
  },
  languageText: {
    fontWeight: 500,
  },
  selectedLanguage: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  }
}));

const LanguageSwitcher = ({ variant = 'default', customStyle = {} }) => {
  const { i18n, t } = useTranslation();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    handleClose();
  };

  // Get current language display name
  const getCurrentLanguageDisplay = () => {
    switch(i18n.language) {
      case 'es':
        return t('language.spanish');
      case 'en':
      default:
        return t('language.english');
    }
  };

  // Button style based on variant
  const buttonStyle = variant === 'login' 
    ? `${classes.languageButton} ${classes.loginLanguageButton}`
    : classes.languageButton;

  return (
    <Box sx={customStyle}>
      <Button
        id="language-button"
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant={variant === 'login' ? "outlined" : "contained"}
        className={buttonStyle}
        endIcon={<KeyboardArrowDownIcon />}
        startIcon={<TranslateIcon />}
        size="small"
      >
        <Typography className={classes.languageText}>
          {getCurrentLanguageDisplay()}
        </Typography>
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        PaperProps={{
          className: classes.menuPaper,
          elevation: 3
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          onClick={() => changeLanguage('en')} 
          className={classes.menuItem}
          selected={i18n.language === 'en'}
        >
          <ListItemText 
            primary={t('language.english')} 
            primaryTypographyProps={{ 
              className: i18n.language === 'en' ? classes.selectedLanguage : ''
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => changeLanguage('es')} 
          className={classes.menuItem}
          selected={i18n.language === 'es'}
        >
          <ListItemText 
            primary={t('language.spanish')} 
            primaryTypographyProps={{ 
              className: i18n.language === 'es' ? classes.selectedLanguage : ''
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher; 